import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { TopNav } from '../components/TopNav';
import { Layers, Zap, ArrowRight, Activity } from 'lucide-react';
import clsx from 'clsx';
import { useLanguage } from '../contexts/LanguageContext';

function PortalLinkCard({ title, subtitle, icon, onClick, accent }) {
    const accentStyles = {
        blue: "from-blue-500/10 to-transparent border-blue-500/20 hover:border-blue-500/50 text-blue-400 ring-blue-500/5",
        purple: "from-purple-500/10 to-transparent border-purple-500/20 hover:border-purple-500/50 text-purple-400 ring-purple-500/5",
        amber: "from-amber-500/10 to-transparent border-amber-500/20 hover:border-amber-500/50 text-amber-400 ring-amber-500/5",
    };

    return (
        <button
            onClick={onClick}
            className={clsx(
                "group relative w-full flex items-center justify-between p-6 bg-slate-900/60 border rounded-3xl transition-all cursor-pointer text-right min-h-[110px] overflow-hidden ring-1 shadow-2xl backdrop-blur-md",
                "bg-gradient-to-r hover:bg-slate-800/80 active:scale-[0.99]",
                accentStyles[accent]
            )}
        >
            <div className="absolute top-0 right-0 w-32 h-32 blur-[80px] opacity-10 pointer-events-none group-hover:opacity-30 transition-opacity" />
            
            <div className="flex flex-col justify-center gap-1.5 z-10 w-full pr-1">
                <div className="flex items-center gap-4">
                    <div className="p-3.5 bg-slate-950/60 rounded-2xl shrink-0 border border-white/5 shadow-inner group-hover:scale-110 transition-transform">
                        {icon}
                    </div>
                    <div className="flex-1 min-w-0 pr-1">
                        <div className="text-xl font-black text-white font-heebo tracking-tight mb-1">{title}</div>
                        <div className="text-[11px] font-black text-slate-500 uppercase tracking-[0.15em] font-inner flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                            {subtitle}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="pl-1 shrink-0 z-10">
                <div className="w-10 h-10 rounded-full bg-slate-950/50 border border-white/5 flex items-center justify-center group-hover:border-white/20 transition-all">
                    <ArrowRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:-translate-x-1 transition-all rtl:rotate-180" />
                </div>
            </div>
        </button>
    );
}

export const MasterLedgerPortal = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const hostId = auth.currentUser?.uid;
    const { t } = useLanguage();

    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!hostId || !id) return;
        const load = async () => {
            try {
                let ref = doc(db, `hosts/${hostId}/games/${id}`);
                let snap = await getDoc(ref);
                if (!snap.exists()) {
                    ref = doc(db, `hosts/${hostId}/sessions/${id}`);
                    snap = await getDoc(ref);
                }
                if (snap.exists()) {
                    setSession({ id: snap.id, ...snap.data() });
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [hostId, id]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col">
                <TopNav />
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!session || session.capacity !== '2 Tables') {
        return (
            <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4">
                <div className="text-slate-400 mb-4 font-heebo">Session is not multi-table or not found.</div>
                <button
                    onClick={() => navigate(-1)}
                    className="px-6 py-2.5 bg-slate-900 border border-white/10 rounded-xl text-white font-bold font-heebo transition-all hover:bg-slate-800"
                >
                    חזרה לסשן
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />

            {/* Header Row */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-slate-900/60 sticky top-0 z-50 backdrop-blur-xl">
                <button
                    onClick={() => navigate(`/session/${id}`)}
                    className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm font-black uppercase tracking-wider font-heebo"
                >
                    <ArrowRight className="w-4 h-4 rtl:rotate-0 rotate-180" />
                    סשן
                </button>
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-black uppercase tracking-widest text-amber-500">Dual Table Hub</span>
                </div>
                <div className="w-10" />
            </div>

            <div className="w-full max-w-xl mx-auto px-4 py-12 relative">
                <div className="text-center mb-10 flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(245,158,11,0.1)]">
                        <Layers className="w-6 h-6 text-amber-500" />
                    </div>
                    <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-l from-white to-slate-400 font-heebo mb-3 tracking-tight">ניהול קרוס-לידג׳ר</h1>
                    <p className="text-sm text-slate-500 font-heebo max-w-[280px]">בחר שולחן לניהול המשחק או בחדר המאסטר לסיכום הכספים.</p>
                </div>

                <div className="space-y-4 relative z-10 w-full">
                    <PortalLinkCard
                        onClick={() => navigate(`/session/${id}/ledger/1`)}
                        icon={<Layers className="w-7 h-7" />}
                        title="שולחן 1"
                        subtitle={session.table1Config?.variant || 'NLH'}
                        accent="blue"
                    />

                    <PortalLinkCard
                        onClick={() => navigate(`/session/${id}/ledger/2`)}
                        icon={<Layers className="w-7 h-7" />}
                        title="שולחן 2"
                        subtitle={session.table2Config?.variant || 'NLH'}
                        accent="purple"
                    />

                    <div className="relative pt-10 mt-6">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 border border-white/10 rounded-full shadow-lg">
                            <Activity className="w-3 h-3 text-amber-500" />
                            Financial Core
                        </div>
                        <PortalLinkCard
                            onClick={() => navigate(`/session/${id}/ledger/master`)}
                            icon={<Zap className="w-7 h-7 text-amber-500" />}
                            title="Master Ledger"
                            subtitle="ניהול פיננסי רוחבי וסיכומי קופה"
                            accent="amber"
                        />
                    </div>
                </div>
            </div>
            
            {/* Background elements */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
                <div className="absolute top-1/4 -right-20 w-[60vw] h-[60vw] bg-blue-500/5 blur-[120px] rounded-full" />
                <div className="absolute bottom-1/4 -left-20 w-[60vw] h-[60vw] bg-amber-500/5 blur-[120px] rounded-full" />
            </div>
        </div>
    );
};

